"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Clock, CircleCheck as CheckCircle2, FileText } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const features = [{
  title: "Solar-specific access",
  description: "Designed for panel lifting clearance and smooth roof-level workflows."
}, {
  title: "Reliable strike windows",
  description: "Prompt dismantling scheduled as soon as PV commissioning is signed off."
}, {
  title: "Full compliance pack",
  description: "Handover certificates, TG20 compliance sheets, and site inspection logs."
}];
const accreditations = [{
  name: "NASC",
  label: "National Access & Scaffolding Confederation"
}, {
  name: "CHAS",
  label: "Accredited Contractor"
}];
export default function Quote() {
  const shouldReduceMotion = useReducedMotion();
  const transitionConfig = shouldReduceMotion ? {
    duration: 0
  } : {
    duration: 0.45,
    ease: [0.16, 1, 0.3, 1]
  };
  return <section id="request-a-quote" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden py-16 md:py-24 lg:py-28 border-t border-border">
      {/* Background industrial grid structure */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--foreground))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--foreground))_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 md:px-12 lg:px-14">
        {/* Structural Datum Line */}
        <div className="w-12 h-1 bg-primary mb-8 rounded-full" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Main Content Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={transitionConfig} className="lg:col-span-7 flex flex-col justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-md bg-secondary/80 px-3 py-1.5 border border-border/40 text-xs font-semibold uppercase tracking-wider text-secondary-foreground mb-4">
                <Clock className="w-3.5 h-3.5 text-primary" />
                <span>Fast quote turnaround across the South East</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight mb-5 font-default">
                Scaffolding built for solar installers.
              </h2>

              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mb-8">
                Plan your next installation with dedicated access designed around panel handling, safe roof transitions, and strict handover dates.
              </p>

              {/* Feature checkpoints */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 pt-4 border-t border-border/40">
                {features.map((item, i) => <div key={item.title} data-index={i} className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                      <span className="text-sm font-semibold text-foreground font-default">{item.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-normal">{item.description}</p>
                  </div>)}
              </div>
            </div>

            {/* Accreditations strip */}
            <div className="pt-6 border-t border-border/30">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                Safety & Compliance Standards
              </p>
              <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                {accreditations.map((badge, i) => <div key={badge.name} data-index={i} className="flex items-center gap-2 rounded-md bg-card/60 px-3 py-2 border border-border/40">
                    <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
                    <div>
                      <span className="text-xs font-bold text-card-foreground block leading-none">{badge.name}</span>
                      <span className="text-[10px] text-muted-foreground block">{badge.label}</span>
                    </div>
                  </div>)}
              </div>
            </div>
          </motion.div>

          {/* Action Card Column */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          ...transitionConfig,
          delay: shouldReduceMotion ? 0 : 0.1
        }} className="lg:col-span-5">
            <div className="rounded-xl bg-card border border-border/60 p-6 sm:p-8 shadow-xl relative overflow-hidden">
              {/* Subtle top edge indicator */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-primary" />

              <h3 className="text-xl font-bold text-card-foreground mb-2 font-default">
                Start your solar quote
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Send site drawings, roof plans, or project specifications for a clear, itemised quotation.
              </p>

              {/* Action Group */}
              <div className="flex flex-col gap-3.5 mb-6">
                <Button asChild size="lg" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-12 shadow-sm justify-between px-5">
                  <Link to="/ContactRequestQuotePage">
                    <span className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Request a quote
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full h-12 justify-between px-5 font-semibold text-foreground border-border bg-secondary/30 hover:bg-secondary/60">
                  <a href="tel:01344929900">
                    <span className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-primary" />
                      Call now
                    </span>
                    <span className="text-xs font-normal text-muted-foreground">01344 929900</span>
                  </a>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full h-12 justify-between px-5 font-medium text-foreground border-border bg-transparent hover:bg-muted/40">
                  <Link to="/ContactRequestQuotePage">
                    <span>Discuss a project</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </Link>
                </Button>
              </div>

              {/* Support note */}
              <div className="bg-muted/40 rounded-lg p-3.5 border border-border/30 text-xs text-muted-foreground">
                <p className="flex items-center gap-2 font-medium text-foreground mb-0.5">
                  <Clock className="w-3.5 h-3.5 text-primary shrink-0" />
                  Operating hours: Monday to Friday, 8am - 6pm
                </p>
                <p className="pl-5.5">
                  Covering Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the South East.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}